import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import warningConfirmModalStyles from './warningConfirmModalStyles';

const WarningConfirmModal = ({
    open,
    toggleModal,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    title = 'Are you sure?',
    subtitle = '',
    maxWidth,
    // Put the keyboard on the cancel button, so Enter backs out instead of
    // confirming. For confirmations that cannot easily be undone.
    focusCancel = false,
    children
}) => {
    const classes = warningConfirmModalStyles();

    return (
        <Dialog
            open={open}
            onClose={() => toggleModal(false)}
            className={classes.dialogPaper}
            // The shared style caps the paper at 420px, which fits the short
            // confirm prompts this dialog was built for but squeezes richer
            // children — a grid, say — into a horizontal scrollbar. Callers can
            // opt into a wider paper; everyone else keeps 420.
            //
            // Inline style on purpose: the class targets `& .MuiPaper-root`, a
            // two-class descendant selector that outranks anything `sx` or a
            // PaperProps className would generate.
            PaperProps={maxWidth ? { style: { maxWidth } } : undefined}
        >
            <Box className={classes.root}>
                <DialogContent style={{ padding: 0 }}>
                    <Typography className={classes.title}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography className={classes.subtitle}>
                            {subtitle}
                        </Typography>
                    )}
                    {children}
                </DialogContent>
                <DialogActions className={classes.buttonsContainer}>
                    {cancelText && (
                        <Button
                            onClick={() => {
                                if (onCancel) {
                                    onCancel();
                                }
                                toggleModal(false);
                            }}
                            className={classes.cancelButton}
                            autoFocus={focusCancel}
                        >
                            {cancelText}
                        </Button>
                    )}

                    {onConfirm && (
                        <Button
                            onClick={onConfirm}
                            variant="contained"
                            color="primary"
                            className={classes.confirmButton}
                            data-testid="confirm_button"
                        >
                            {confirmText}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};

WarningConfirmModal.propTypes = {
    open: PropTypes.bool.isRequired,
    toggleModal: PropTypes.func.isRequired,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    focusCancel: PropTypes.bool,
    children: PropTypes.any
};

export default memo(WarningConfirmModal);
